<script lang="ts" setup>
import { AvvStatus } from '@/types'
import { STATUS_CODES } from '@/constants'
import { onMounted, onUnmounted, ref, computed } from 'vue'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { acceptAvv, avvRefusalKey, AvvRequestError, fetchAvvPdf } from '@/composables/useAvv'
import { uuid, LOADING_SIZES, LOADING_WIDTH } from '@/composables'
import { useNotifyStore, useJwtStore, useTermsStore } from '@/stores'
import { DateTime } from 'luxon'
import { useI18n } from 'vue-i18n'
import { appConfig } from 'config/app'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'

const { t } = useI18n()
const notifyStore = useNotifyStore()
const jwtStore = useJwtStore()
const termsStore = useTermsStore()

const loading = ref(true)
const accepting = ref(false)
const status = ref<AvvStatus | null>(null)
const error = ref<string | null>(null)
const emailNotVerified = ref(false)
const pdfBlobUrl = ref<string | null>(null)
const pdfLoading = ref(false)

const formatDate = (dateString: string): string => {
  const dt = DateTime.fromISO(dateString)
  return dt.toLocaleString(DateTime.DATE_FULL, { locale: 'de' })
}

const needsAcceptance = computed(() => {
  if (!status.value) return false
  return !status.value.accepted || status.value.needs_reaccept
})

const fetchStatus = async () => {
  loading.value = true
  error.value = null
  emailNotVerified.value = false

  try {
    const res = await useAxios<null, AvvStatus>('GET', '/account/av/status', true)
    if (!res) return

    if (res.status === STATUS_CODES.OK) {
      const validate = AvvStatus.safeParse(res.data)
      if (!validate.success) {
        error.value = t('dashboard.account.avv.error.loadFailed')
        return
      }
      status.value = validate.data
      // Keeps the banner's AVV prompt in step once the agreement is accepted here.
      termsStore.setAvvStatus(validate.data)

      // Show the stored evidence PDF once accepted, the preview before.
      //
      // This always fetched ?preview=true, which regenerates from current data
      // and current text — so an accepted customer was shown a freshly
      // rendered document rather than the one they actually agreed to.
      await fetchPdf(validate.data.accepted && !validate.data.needs_reaccept)
    }
  } catch (err: unknown) {
    handleAxiosError(err, { logPrefix: 'AccountPageAvv' })
    const refusal = avvRefusalKey(err)
    if (refusal === 'dashboard.account.avv.emailNotVerified') {
      emailNotVerified.value = true
    } else {
      error.value = t(refusal ?? 'dashboard.account.avv.error.loadFailed')
    }
  } finally {
    loading.value = false
  }
}

const fetchPdf = async (accepted: boolean) => {
  pdfLoading.value = true
  try {
    const url = URL.createObjectURL(await fetchAvvPdf(accepted))
    if (pdfBlobUrl.value) URL.revokeObjectURL(pdfBlobUrl.value)
    pdfBlobUrl.value = url
  } catch (err) {
    if (err instanceof AvvRequestError) {
      if (err.status === 403) {
        emailNotVerified.value = true
      }
      return
    }
    handleAxiosError(err, { logPrefix: 'AccountPageAvv:fetchPdf' })
  } finally {
    pdfLoading.value = false
  }
}

const retryPdf = () => fetchPdf(!needsAcceptance.value)

// Accepting needs the document on screen: one that failed to load offers a retry instead.
const accept = async () => {
  if (accepting.value || !pdfBlobUrl.value) return
  accepting.value = true
  error.value = null

  try {
    const accepted = await acceptAvv()
    if (!accepted) {
      error.value = t('dashboard.account.avv.error.acceptFailed')
      return
    }

    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'success',
      title: t('dashboard.account.avv.acceptedTitle'),
      message: t('dashboard.account.avv.acceptedInfo', { date: formatDate(accepted.accepted_at) })
    })

    // Clears the banner even when the re-read below fails.
    if (status.value) {
      termsStore.setAvvStatus({
        ...status.value,
        accepted: true,
        accepted_at: accepted.accepted_at,
        accepted_version: accepted.version,
        needs_reaccept: false
      })
    }
    await fetchStatus()
  } catch (err: unknown) {
    handleAxiosError(err, { logPrefix: 'AccountPageAvv:accept' })
    const refusal = avvRefusalKey(err)
    if (refusal === 'dashboard.account.avv.emailNotVerified') {
      emailNotVerified.value = true
      error.value = t(refusal)
    } else if (refusal) {
      error.value = t(refusal)
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'error',
        title: t('common.error'),
        message: error.value
      })
    } else {
      error.value = t('dashboard.account.avv.error.acceptFailed')
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'error',
        title: t('common.error'),
        message: getErrorMessage(err)
      })
    }
  } finally {
    accepting.value = false
  }
}

const downloadPdf = () => {
  const token = jwtStore.getJwt
  const url = `${appConfig.env.baseUrl}/account/av?download=true`

  fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  })
    .then((res) => {
      if (!res.ok) throw new AvvRequestError(res.status)
      return res.blob()
    })
    .then((blob) => {
      const blobUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = 'AVV.pdf'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(blobUrl)
    })
    .catch((err) => {
      handleAxiosError(err, { logPrefix: 'AccountPageAvv:download' })
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'error',
        title: t('common.error'),
        message: getErrorMessage(err)
      })
    })
}

onMounted(() => {
  fetchStatus()
})

onUnmounted(() => {
  if (pdfBlobUrl.value) {
    URL.revokeObjectURL(pdfBlobUrl.value)
  }
})
</script>

<template>
  <BaseDashboardPage
    :title="$t('dashboard.account.avv.title')"
    icon="mdi-file-sign"
    icon-color="cyan"
  >
    <div class="avv-page">
      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <v-progress-circular indeterminate size="48" width="3" color="primary" />
        <p>{{ $t('dashboard.account.avv.loading') }}</p>
      </div>

      <!-- Email Not Verified -->
      <div v-else-if="emailNotVerified" class="email-verification-required">
        <div class="verification-icon">
          <v-icon icon="mdi-email-alert" size="48" />
        </div>
        <h3>{{ $t('dashboard.account.avv.emailNotVerified') }}</h3>
        <router-link :to="{ name: 'dashboardAccount', params: { tab: 'details' } }" class="action-btn primary">
          {{ $t('dashboard.account.tabs.details') }}
        </router-link>
      </div>

      <!-- Error State -->
      <div v-else-if="error && !status" class="error-state">
        <v-alert type="error" variant="tonal" class="error-alert">
          {{ error }}
        </v-alert>
        <button class="action-btn primary" @click="fetchStatus">
          <v-icon icon="mdi-refresh" size="18" />
          <span>{{ $t('dashboard.account.avv.error.retry') }}</span>
        </button>
      </div>

      <!-- Content -->
      <template v-else-if="status">
        <!-- Needs Acceptance -->
        <div v-if="needsAcceptance" class="acceptance-section">
          <!-- New Version Banner -->
          <v-alert
            v-if="status.accepted && status.needs_reaccept"
            type="info"
            variant="tonal"
            class="new-version-alert"
          >
            <template #prepend>
              <v-icon icon="mdi-information" />
            </template>
            {{ $t('dashboard.account.avv.newVersionAvailable') }}
          </v-alert>

          <div class="preview-header">
            <div class="header-icon">
              <v-icon icon="mdi-file-document-outline" size="20" />
            </div>
            <div class="header-text">
              <h2 class="section-title">{{ $t('dashboard.account.avv.previewTitle') }}</h2>
              <p class="section-subtitle">{{ $t('dashboard.account.avv.subtitle') }}</p>
            </div>
          </div>

          <!-- PDF Preview -->
          <div class="pdf-preview-container">
            <div v-if="pdfLoading" class="pdf-loading">
              <v-progress-circular indeterminate size="32" width="2" color="primary" />
            </div>
            <iframe
              v-else-if="pdfBlobUrl"
              :src="pdfBlobUrl"
              class="pdf-iframe"
              title="AVV Preview"
            />
            <div v-else class="pdf-error" data-test="avv-pdf-error">
              <v-icon icon="mdi-file-document-alert" size="32" />
              <span>{{ $t('dashboard.account.avv.error.pdfFailed') }}</span>
              <button class="action-btn primary" data-test="avv-retry" @click="retryPdf">
                <v-icon icon="mdi-refresh" size="18" />
                <span>{{ $t('dashboard.account.avv.error.retry') }}</span>
              </button>
            </div>
          </div>

          <!-- Accept Button -->
          <div class="accept-actions">
            <button
              class="action-btn primary accept-btn"
              data-test="avv-accept"
              :disabled="accepting || pdfLoading || !pdfBlobUrl"
              @click="accept"
            >
              <v-progress-circular
                v-if="accepting"
                indeterminate
                :size="LOADING_SIZES.button"
                :width="LOADING_WIDTH"
              />
              <template v-else>
                <v-icon icon="mdi-check" size="18" />
                <span>{{ $t('dashboard.account.avv.acceptButton') }}</span>
              </template>
            </button>
          </div>
        </div>

        <!-- Already Accepted -->
        <div v-else class="accepted-section">
          <!-- Status Header -->
          <div class="accepted-header">
            <div class="accepted-badge">
              <v-icon icon="mdi-check-circle" size="20" />
              <span>{{ $t('dashboard.account.avv.acceptedTitle') }}</span>
            </div>
            <p class="accepted-info">
              {{ $t('dashboard.account.avv.acceptedInfo', { date: formatDate(status.accepted_at!) }) }}
            </p>
          </div>

          <!-- Signed PDF Preview -->
          <div class="pdf-preview-container">
            <div v-if="pdfLoading" class="pdf-loading">
              <v-progress-circular indeterminate size="32" width="2" color="primary" />
            </div>
            <iframe
              v-else-if="pdfBlobUrl"
              :src="pdfBlobUrl"
              class="pdf-iframe"
              title="Signed AVV"
            />
            <div v-else class="pdf-error" data-test="avv-pdf-error">
              <v-icon icon="mdi-file-document-alert" size="32" />
              <span>{{ $t('dashboard.account.avv.error.pdfFailed') }}</span>
              <button class="action-btn primary" data-test="avv-retry" @click="retryPdf">
                <v-icon icon="mdi-refresh" size="18" />
                <span>{{ $t('dashboard.account.avv.error.retry') }}</span>
              </button>
            </div>
          </div>

          <!-- Download Action -->
          <div class="accepted-actions">
            <button class="action-btn primary download-btn" @click="downloadPdf">
              <v-icon icon="mdi-download" size="18" />
              <span>{{ $t('dashboard.account.avv.downloadButton') }}</span>
            </button>
          </div>
        </div>
      </template>
    </div>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
.avv-page {
  background: rgba(15, 23, 48, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: var(--dashboard-card-radius);
  padding: var(--dashboard-section-margin);
  min-height: 400px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 60px 24px;
  color: rgba(200, 218, 255, 0.6);

  p {
    font-size: 0.9rem;
    margin: 0;
  }
}

.email-verification-required {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 60px 24px;

  .verification-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 88px;
    height: 88px;
    background: rgba(255, 152, 0, 0.12);
    border-radius: 20px;
    color: #ffb74d;
    margin-bottom: 20px;
  }

  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    margin: 0 0 20px;
  }
}

.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 40px 24px;

  .error-alert {
    max-width: 400px;
  }
}

.acceptance-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.new-version-alert {
  margin-bottom: 8px;
}

.preview-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.08);
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, rgba(0, 188, 212, 0.15), rgba(0, 188, 212, 0.08));
  border: 1px solid rgba(0, 188, 212, 0.2);
  border-radius: 10px;
  color: #4dd0e1;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 2px;
}

.section-subtitle {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);
  margin: 0;
}

.pdf-preview-container {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
  min-height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pdf-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
}

.pdf-iframe {
  width: 100%;
  height: 600px;
  border: none;
  background: white;
}

.pdf-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: rgba(200, 218, 255, 0.4);
  padding: 60px;

  span {
    font-size: 0.9rem;
  }
}

.accept-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid rgba(120, 163, 255, 0.08);
}

.accepted-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.accepted-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.08);
}

.accepted-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: rgba(76, 175, 80, 0.12);
  border: 1px solid rgba(76, 175, 80, 0.25);
  border-radius: 8px;
  color: #81c784;
  font-weight: 500;
  font-size: 0.9rem;
}

.accepted-info {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.6);
  margin: 0;
}

.accepted-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid rgba(120, 163, 255, 0.08);
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  text-decoration: none;

  &.primary {
    background: rgba(0, 188, 212, 0.15);
    border: 1px solid rgba(0, 188, 212, 0.25);
    color: #4dd0e1;

    &:hover:not(:disabled) {
      background: rgba(0, 188, 212, 0.25);
      border-color: rgba(0, 188, 212, 0.4);
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.accept-btn {
  min-width: 240px;
}

.download-btn {
  background: rgba(76, 175, 80, 0.15);
  border: 1px solid rgba(76, 175, 80, 0.25);
  color: #81c784;

  &:hover:not(:disabled) {
    background: rgba(76, 175, 80, 0.25);
    border-color: rgba(76, 175, 80, 0.4);
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .avv-page {
    background: rgba(255, 255, 255, 0.7);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .section-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .section-subtitle, .accepted-info {
    color: rgba(15, 23, 42, 0.6);
  }

  .accepted-header, .accept-actions, .accepted-actions {
    border-color: rgba(28, 110, 227, 0.08);
  }

  .email-verification-required h3 {
    color: rgba(15, 23, 42, 0.9);
  }

  .loading-state {
    color: rgba(15, 23, 42, 0.6);
  }

  .pdf-preview-container {
    background: rgba(0, 0, 0, 0.03);
    border-color: rgba(28, 110, 227, 0.1);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .avv-page {
    padding: 20px;
    border-radius: 16px;
  }

  .pdf-iframe {
    height: 400px;
  }

  .accepted-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .accept-actions,
  .accepted-actions {
    justify-content: center;
  }

  .accept-btn,
  .download-btn {
    width: 100%;
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .action-btn {
    transition: none;
  }
}
</style>
